import { useState } from 'react';
import { ImagePlus, Images, Trash2 } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import { uploadFormImage } from '../../../lib/upload';
import { Button } from '../../ui/Button';
import { MediaLibraryModal } from './MediaLibraryModal';

/**
 * Picks an image, uploads it immediately to the shared form-image store and
 * stores the serving URL — never base64 in the schema, or the 700ms draft
 * autosave would re-send megabytes per keystroke.
 */
export function ImageUploadInput({
  value,
  onChange,
  label = 'Image',
}: {
  value: string | undefined;
  onChange: (url: string | undefined) => void;
  label?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      onChange(await uploadFormImage(file));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="text-sm">
      <span className="font-medium text-fg-muted">{label}</span>
      <div className="mt-1 flex flex-wrap items-center gap-3">
        {value ? (
          <img
            src={value}
            alt=""
            className="h-16 w-24 shrink-0 rounded-token-sm border border-line/10 object-cover"
          />
        ) : null}
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg hover:bg-surface-2">
          <ImagePlus className="h-4 w-4" />
          {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload image'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => void onPick(e)}
            disabled={uploading}
          />
        </label>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Images className="h-4 w-4" />}
          onClick={() => setLibraryOpen(true)}
        >
          Library
        </Button>
        {value ? (
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Trash2 className="h-4 w-4" />}
            onClick={() => onChange(undefined)}
          >
            Remove
          </Button>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
      <MediaLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onPick={(url) => onChange(url)}
      />
    </div>
  );
}
